import { NAMESPACE } from "./catalog";
import type { Ability } from "./types";

const CASTER_TAG = `${NAMESPACE}.caster`;
const NEW_TAG = `${NAMESPACE}.new`;
const NOT_TARGETS = [
  "player", "item", "experience_orb", "item_frame", "glow_item_frame", "armor_stand", "painting",
  "wolf", "iron_golem", "snow_golem",
].map((t) => `type=!${t}`).join(",");
const ALLY_OFFSETS = ["~1 ~ ~", "~-1 ~ ~", "~ ~ ~1"];
const LEVITATION_PER_POWER = 4;

export function abilityCommands(ability: Ability): string[] {
  switch (ability.type) {
    case "buff":
      return [
        ...ability.effects.map((e) => `effect give @s minecraft:${e.effect} ${e.seconds} ${e.level - 1}`),
        sound("minecraft:block.beacon.power_select", 1.5),
        "execute at @s run particle minecraft:totem_of_undying ~ ~1 ~ 0.5 0.8 0.5 0.2 30",
      ];
    case "heal":
      return [
        `effect give @s minecraft:instant_health 1 ${ability.power - 1}`,
        sound("minecraft:entity.player.levelup", 1.8),
        "execute at @s run particle minecraft:heart ~ ~2 ~ 0.5 0.4 0.5 0 8",
      ];
    case "leap":
      return [
        `effect give @s minecraft:levitation 1 ${ability.power * LEVITATION_PER_POWER}`,
        "effect give @s minecraft:slow_falling 8 0",
        sound("minecraft:entity.breeze.jump", 1),
        "execute at @s run particle minecraft:gust ~ ~ ~ 0.3 0.1 0.3 0 3",
      ];
    case "blink":
      return [
        "execute at @s run particle minecraft:portal ~ ~1 ~ 0.3 0.6 0.3 0.5 40",
        `execute at @s rotated ~ 0 positioned ^ ^ ^${ability.distance} if block ~ ~ ~ minecraft:air if block ~ ~1 ~ minecraft:air run tp @s ~ ~ ~`,
        sound("minecraft:entity.enderman.teleport", 1),
      ];
    case "lightning":
      return [`execute at @s rotated ~ 0 positioned ^ ^ ^${ability.distance} run summon minecraft:lightning_bolt ~ ~ ~`];
    case "meteor":
      return [
        `execute at @s rotated ~ 0 positioned ^ ^ ^${ability.distance} run summon minecraft:fireball ~ ~20 ~ ` +
        `{ExplosionPower:${ability.power}b,Motion:[0.0d,-1.0d,0.0d],acceleration_power:0.1d}`,
        sound("minecraft:entity.ghast.shoot", 0.6),
      ];
    case "shockwave":
      return [
        `tag @s add ${CASTER_TAG}`,
        `execute at @s as @e[${NOT_TARGETS},distance=..${ability.radius}] run damage @s ${ability.damage} minecraft:player_attack by @p[tag=${CASTER_TAG}]`,
        `execute at @s run effect give @e[${NOT_TARGETS},distance=..${ability.radius}] minecraft:levitation 1 1`,
        `tag @s remove ${CASTER_TAG}`,
        `execute at @s run particle minecraft:explosion ~ ~1 ~ ${ability.radius / 2} 0.3 ${ability.radius / 2} 0 15`,
        sound("minecraft:entity.generic.explode", 1.2),
      ];
    case "summon_allies":
      return [...Array(ability.count).keys()].flatMap((i) => summonAlly(ability.mob, ALLY_OFFSETS[i % ALLY_OFFSETS.length]));
  }
}

function summonAlly(mob: string, offset: string): string[] {
  if (mob === "wolf") {
    return [
      `execute at @s run summon minecraft:wolf ${offset} {Tags:["${NEW_TAG}"]}`,
      `data modify entity @e[type=minecraft:wolf,tag=${NEW_TAG},limit=1] Owner set from entity @s UUID`,
      `tag @e[tag=${NEW_TAG}] remove ${NEW_TAG}`,
    ];
  }
  const data = mob === "iron_golem" ? " {PlayerCreated:true}" : "";
  return [`execute at @s run summon minecraft:${mob} ${offset}${data}`];
}

function sound(id: string, pitch: number): string {
  return `execute at @s run playsound ${id} player @a ~ ~ ~ 1 ${pitch}`;
}
